import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { DeleteAssetTypeModal } from "@/components/Organisms/DeleteAssetTypeModal/DeleteAssetTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { EMPTY_ORG_ID } from "@/constants/org";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	useDuplicateAssetType,
	useUpdateAssetType,
} from "@/services/asset/asset.mutations";
import { assetQueryOptions } from "@/services/asset/asset.options";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	canShowActivityTab,
	getAssetTypeDisplayName,
} from "@/utils/asset-type-detail/asset-type-detail.utils";
import {
	AssetTypeDrawerContext,
	type AssetTypeDrawerContextValue,
} from "@/utils/asset-type-detail/asset-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type AssetTypeTab = "details" | "schema" | "lifecycle" | "rules" | "activity";

function getCurrentTab(pathname: string): AssetTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/assetTypes/$assetTypeId",
)({
	component: AdminAssetTypeIdLayout,
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/assetTypes/${params.assetTypeId}`,
		);
		if (exactRoot) {
			throw redirect({
				to: "/admin/configuration/assetTypes/$assetTypeId/details",
				params: { assetTypeId: params.assetTypeId },
				search: { mode: "view", presentation: "drawer" },
			});
		}
	},
});

function AdminAssetTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFormRoute = useRouterState({
		select: (s) =>
			s.matches.some(
				(m) =>
					typeof m.routeId === "string" &&
					m.routeId.endsWith("/$assetTypeId/form"),
			),
	});
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/assetTypes/$assetTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/assetTypes/$assetTypeId",
	});

	const isFullRoute = /\/configuration\/assetTypes\/[^/]+\/full/.test(pathname);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		assetQueryOptions.typeDetail(params.assetTypeId, {
			organizationId: EMPTY_ORG_ID,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});
	const updateMutation = useUpdateAssetType();
	const duplicateMutation = useDuplicateAssetType();
	const { duplicate: duplicateAssetType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (assetTypeId) => ({
				assetTypeId,
				organizationId: EMPTY_ORG_ID,
			}),
			onDuplicated: (assetTypeId) => {
				void navigate({
					to: "/admin/configuration/assetTypes/$assetTypeId/details",
					params: { assetTypeId },
					search: { mode: "view", presentation: "drawer" },
				});
			},
			errorMessage: t("admin.assetTypes.modal.createError"),
		});

	const assetType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		assetType?.id,
		assetType?.locationRequired,
	);
	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");

	useEffect(() => {
		if (!assetType) return;
		const name = getAssetTypeDisplayName(assetType);
		const description = assetType.description ?? "";
		const icon = assetType.icon ?? "";
		if (search.mode !== "edit" || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (search.mode !== "edit" || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (search.mode !== "edit" || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
	}, [assetType]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/admin/configuration/assetTypes",
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || assetType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && assetType !== null;

	const loadedAssetType = assetType ?? undefined;
	const displayAssetType =
		loadedAssetType && isDetailReady
			? {
					...loadedAssetType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedAssetType.icon,
				}
			: null;

	const canEdit = !!loadedAssetType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedAssetType &&
		isDetailReady &&
		canShowActivityTab("platform", loadedAssetType);
	const currentTab = getCurrentTab(pathname);
	const isEditing = search.mode === "edit";
	const isValid = draftName.trim().length > 0;

	const tabs: { id: AssetTypeTab; label: string }[] = isDetailReady
		? [
				{ id: "details", label: t("admin.assetTypes.drawer.tabs.details") },
				{ id: "schema", label: t("admin.assetTypes.drawer.tabs.form") },
				{ id: "lifecycle", label: t("admin.assetTypes.drawer.tabs.lifecycle") },
				{ id: "rules", label: t("admin.assetTypes.drawer.tabs.rules") },
				...(showActivity
					? [
							{
								id: "activity" as const,
								label: t("admin.assetTypes.drawer.tabs.activity"),
							},
						]
					: []),
			]
		: [];
	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedAssetType && isDetailReady && !!canDeleteQuery.data && !isEditing;
	const canDuplicate = isDetailReady && !isEditing;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedAssetType || !isValid) return;
		try {
			await updateMutation.mutateAsync({
				assetTypeId: loadedAssetType.id,
				data: {
					name: draftName.trim(),
					description: draftDescription || null,
					icon: draftIcon || null,
					typeId: loadedAssetType.id,
					statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
					geometryType: loadedAssetType.geometryType ?? null,
					locationRequired,
				},
				organizationId: EMPTY_ORG_ID,
			});
			setEditedName(draftName.trim());
			setEditedDescription(draftDescription);
			setEditedIcon(draftIcon);
			showSuccessToast({
				message: t("admin.assetTypes.drawer.details.toast.saveSuccess"),
			});
			void navigate({
				to: `/admin/configuration/assetTypes/${params.assetTypeId}/details`,
				search: { mode: "view" },
			});
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedAssetType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		await updateMutation.mutateAsync({
			assetTypeId: loadedAssetType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedAssetType.id,
				statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
				geometryType: loadedAssetType.geometryType ?? null,
				locationRequired,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedAssetType) return;
		await updateMutation.mutateAsync({
			assetTypeId: loadedAssetType.id,
			data: {
				name: (editedName || loadedAssetType.name || "").trim(),
				description: editedDescription || loadedAssetType.description || null,
				icon: editedIcon || loadedAssetType.icon || null,
				typeId: loadedAssetType.id,
				statusLifecycleId: loadedAssetType.statusLifecycle?.id ?? null,
				geometryType: loadedAssetType.geometryType ?? null,
				locationRequired: next,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setLocationRequired(next);
	};

	const contextValue: AssetTypeDrawerContextValue | null =
		loadedAssetType && isDetailReady && displayAssetType
			? {
					scope: "platform",
					assetType: displayAssetType,
					mode: search.mode,
					activeTab: currentTab,
					setMode: (mode) => {
						if (isFullRoute) {
							void navigate({
								to: `/admin/configuration/assetTypes/${params.assetTypeId}/full`,
								search: { tab: "details", mode },
							});
						} else {
							void navigate({
								to: `/admin/configuration/assetTypes/${params.assetTypeId}/${currentTab}`,
								search: { mode },
							});
						}
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds: null,
					setDraftSubOrgIds: () => {},
					isSaving: updateMutation.isPending,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						void navigate({
							to: `/admin/configuration/assetTypes/${params.assetTypeId}/details`,
							search: { mode: "view" },
						});
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayAssetType !== null
			? getAssetTypeDisplayName(displayAssetType)
			: t("admin.assetTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedAssetType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedAssetType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedAssetType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedAssetType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	if (isFullRoute) {
		return contextValue ? (
			<AssetTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</AssetTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	const isOnAssetTypeSubroute = /\/configuration\/assetTypes\/[^/]+\//.test(
		pathname,
	);
	if (!isOnAssetTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="asset"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: `/admin/configuration/assetTypes/${params.assetTypeId}/full`,
						search: { tab: "details", mode: "view" },
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.assetTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateAssetType(params.assetTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.assetTypes.drawer.footer.deleteAssetType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.assetTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.assetTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/admin/configuration/assetTypes/${params.assetTypeId}/${tabId}`,
						search: { mode: search.mode },
					})
				}
				data-testid="asset-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<AssetTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</AssetTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteAssetTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				assetTypeId={params.assetTypeId}
				assetTypeName={
					loadedAssetType ? getAssetTypeDisplayName(loadedAssetType) : ""
				}
				organizationId={EMPTY_ORG_ID}
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.assetTypes.duplicate.inProgress")}
				testId="assettype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
